import { useEffect, useRef, useState } from 'react';
import NotificationService from '../../services/NotificationService';
import { getQueryResultStatus, useQueryResult } from './useQueryResult';
import { useQueryCount } from './useQueryCount';
import { useQueryResultRefresh } from './useQueryResultRefresh';

const POLL_INTERVAL_MS = 5000;
// Juste après le lancement d'un comptage, DQS répond « UNKNOWN OR EXPIRED » tant que la tâche n'est pas
// enregistrée : pendant ce délai, ce statut est traité comme un comptage en cours et non comme une expiration.
const LAUNCH_GRACE_MS = 60000;

export const useTrackedQueryResult = (open: boolean, queryId: string | number | null, starting = false) => {
    const { fetchQueryResult, loading, error, result, reset, markAsRunning } = useQueryResult();
    const { launchQueryCount, loading: relaunching, error: relaunchError, reset: resetCount } = useQueryCount();
    const [launchedAt, setLaunchedAt] = useState<number | null>(null);
    const launchingRef = useRef(false);
    if (open && starting) launchingRef.current = true;

    const rawStatus = getQueryResultStatus(result);
    const justLaunched = launchedAt !== null && Date.now() - launchedAt < LAUNCH_GRACE_MS;
    const status = justLaunched && (rawStatus === 'expired' || rawStatus === 'unknown') ? 'pending' : rawStatus;
    const isPending = status === 'pending';
    const isCompleted = status === 'completed';

    useEffect(() => {
        if (!open) {
            launchingRef.current = false;
            setLaunchedAt(null);
            reset();
            resetCount();
            return;
        }
        if (!queryId) return;
        if (launchingRef.current) {
            // Comptage que l'on vient de lancer : on attend son résultat au lieu de lire un état pas encore prêt
            launchingRef.current = false;
            setLaunchedAt(Date.now());
            markAsRunning();
            return;
        }
        fetchQueryResult(queryId);
    }, [open, queryId, fetchQueryResult, markAsRunning, reset, resetCount]);

    useEffect(() => {
        if (rawStatus === 'completed' || rawStatus === 'error') setLaunchedAt(null);
    }, [rawStatus]);

    useEffect(() => {
        if (isCompleted && queryId) NotificationService.refreshFromRest().catch(() => {});
    }, [isCompleted, queryId]);

    const watchedQueryId = open && queryId ? queryId : null;
    useQueryResultRefresh(
        watchedQueryId,
        () => { if (watchedQueryId) fetchQueryResult(watchedQueryId, { keepPreviousResult: true }); },
        isPending ? POLL_INTERVAL_MS : null,
    );

    const relaunchCount = async () => {
        if (!queryId) return;
        const response = await launchQueryCount(queryId);
        if (response?.taskId) {
            setLaunchedAt(Date.now());
            markAsRunning(response.taskId);
        }
        else if (response?.count !== undefined) await fetchQueryResult(queryId);
    };

    return { result, status, loading, error: error || relaunchError, relaunching, relaunchCount };
};
